import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AppCookie, clearBearerToken } from "@bridgesplit/react";
import { useSelector } from "react-redux";

import { ABF_GROUP_COOKIE_PREFIX, AUTH0_COOKIES_PREFIX, USER_WALLET_COOKIE } from "../constants";
import { normalizePrivyWalletClientType } from "../wallet/privyHelpers";

// Import at the top of the file

export type AuthSliceState = {
    apiVersionUpdateNeeded: boolean;
    stateWalletAddress: string | undefined;
    walletClientType: string | undefined; // can not use privy SOLANA_WALLET_CLIENT_TYPES because it is incomplete.
};

const initialState: AuthSliceState = {
    apiVersionUpdateNeeded: false,
    stateWalletAddress: undefined,
    walletClientType: undefined
};
const authSlice = createSlice({
    name: "authSlice",
    initialState,
    reducers: {
        resetAuthState: () => {
            return {
                apiVersionUpdateNeeded: false,
                stateWalletAddress: undefined,
                walletClientType: undefined
            };
        },
        updateApiVersionNeeded: (state, action: PayloadAction<boolean>) => {
            state.apiVersionUpdateNeeded = action.payload;
        },
        updateStateWalletAddress: (
            state,
            action: PayloadAction<{ address: string | undefined; walletClientType: string | undefined }>
        ) => {
            state.stateWalletAddress = action.payload.address;
            state.walletClientType = normalizePrivyWalletClientType(action.payload.walletClientType);
        }
    }
});

export default authSlice.reducer;
export const { updateApiVersionNeeded, updateStateWalletAddress } = authSlice.actions;

export const getSlice = (state: { authSlice: AuthSliceState }): AuthSliceState => state.authSlice;

/**
 * Set values in cookies and reducer
 * Cookie storage allows credentials to be used in authenticated RTK queries
 * Reducer storage allows credentials to be used to trigger hook re-renders
 */

function resetCookies() {
    clearBearerToken(AUTH0_COOKIES_PREFIX);
    clearBearerToken(ABF_GROUP_COOKIE_PREFIX);
}

function setWalletCookie(walletAddress: string, walletClientType: string | undefined) {
    const normalizedWalletClientType = normalizePrivyWalletClientType(walletClientType);
    const walletCookie = JSON.stringify({ address: walletAddress, clientType: normalizedWalletClientType });
    AppCookie.set(USER_WALLET_COOKIE, walletCookie);
}

function getWalletCookieAddress() {
    const cookie = AppCookie.get(USER_WALLET_COOKIE);
    if (!cookie) return undefined;
    try {
        return JSON.parse(cookie).address;
    } catch {
        return undefined;
    }
}

function getWalletCookieClientType() {
    const cookie = AppCookie.get(USER_WALLET_COOKIE);
    if (!cookie) return undefined;
    try {
        return JSON.parse(cookie).clientType;
    } catch {
        return undefined;
    }
}

export function useAuthStorage() {
    const state: AuthSliceState = useSelector(getSlice);

    return {
        state,
        resetCookies,
        setWalletCookie,
        getWalletCookieAddress,
        getWalletCookieClientType
    };
}
